import { Component, Input, computed, signal } from '@angular/core';
import { EndClientReportService } from '../end-client-report.service';
import { ThemePreviewReportService, ThemePreviewSource } from './theme-preview-report.service';
import type { ColorPalette, ReportLogo } from '../../../models/end-client-reporting-package';

/**
 * NK-450 — a deck drawn in a theme's colours, for the theme editor.
 *
 * The real deck component, fed by `ThemePreviewReportService` and left in client view -- so the
 * preview is exactly what an end client would see, rather than an imitation of it. Its own
 * component because the service swap has to happen *around* the deck: the editor holding this
 * needs the real service to save the theme.
 */
@Component({
  selector: 'or-theme-preview',
  standalone: false,
  templateUrl: './theme-preview.component.html',
  styleUrls: ['./theme-preview.component.scss'],
  providers: [
    ThemePreviewReportService,
    { provide: EndClientReportService, useExisting: ThemePreviewReportService },
  ],
})
export class ThemePreviewComponent {
  /** What to draw. Changing it re-creates the deck, which loads its data once, on init. */
  @Input() set source(value: ThemePreviewSource | null | undefined) {
    const next = value ?? { kind: 'sample' };
    this.previewService.source = next;
    this.renderKey.set(next.kind === 'sample' ? 'sample' : `${next.kind}:${next.id}`);
  }

  /** The colours being edited. Applied live, without reloading the deck. */
  @Input() palette: ColorPalette | null = null;

  /** The logo being edited (NK-518), in place of the deck's own; null for none. */
  @Input() logo: ReportLogo | null = null;

  /** One entry, keyed on the source, so a `@for` re-creates the deck when the source changes. */
  private renderKey = signal('sample');
  renderKeys = computed(() => [this.renderKey()]);

  constructor(private previewService: ThemePreviewReportService) { }
}
